import {
  useEffect, useState
} from 'react'
import {
  getApiErrorMessage,
  scopeService
} from '../../services/api'
import type {
  ScopeStatusResponse
} from '../../services/api'
import {
  OnboardingWizardEvents,
  trackEvent
} from '../../analytics/posthog'
import {
  PartialGrantNotice
} from './PartialGrantNotice'
import {
  hasScopeCluster,
} from '../../utils/scopeClusters'
import {
  PermissionsDeferOption
} from './PermissionsDeferOption'
import {
  usePermissionsCopyArm
} from './usePermissionsCopyArm'
import {
  Calendar,
  Check,
  FileText,
  Loader2,
  Mail,
  ShieldCheck,
  Video
} from 'lucide-react'

// Outcomes, not scopes: the 'test' arm of the copy experiment (GET-19510). The
// skip rate rose to ~66% while the step explained itself in one generic line.
const MAIL_CALENDAR_BENEFITS = [
  'Finds times and books meetings around your real calendar',
  'Flags the emails that need you and drafts replies in your voice',
  'Preps you before meetings and sends a morning brief of your day',
]

interface PermissionsStepProps {
  scopeStatus: ScopeStatusResponse | null
  onContinue: () => void
  onDefer: () => void
  incompleteClusters: string[]
  scopeUpgradeFailed?: boolean
  assistantName?: string | undefined
}

export function PermissionsStep({
  scopeStatus,
  onContinue,
  onDefer,
  incompleteClusters,
  scopeUpgradeFailed,
  assistantName,
}: PermissionsStepProps): JSX.Element {
  const [includeMeet, setIncludeMeet] = useState(true)
  const [includeDocs, setIncludeDocs] = useState(true)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const hasMailCalendar =
    scopeStatus !== null && hasScopeCluster(scopeStatus, 'mail_calendar')
  const { arm, enrolled } = usePermissionsCopyArm()
  const isTest = arm === 'test'

  useEffect(() => {
    trackEvent(OnboardingWizardEvents.PERMISSIONS_STEP_VIEWED, {
      copy_arm: arm,
      copy_enrolled: enrolled,
    })
  }, [arm, enrolled])

  useEffect(() => {
    if (scopeUpgradeFailed) {
      trackEvent(OnboardingWizardEvents.PERMISSIONS_INCOMPLETE)
    }
  }, [scopeUpgradeFailed])

  const handleGrantAccess = async (): Promise<void> => {
    const clusters = ['mail_calendar']
    if (includeMeet) clusters.push('meet')
    if (includeDocs) clusters.push('docs')

    trackEvent(OnboardingWizardEvents.PERMISSIONS_GRANT_CLICKED, {
      clusters,
    })

    setLoading(true)
    setError(null)
    try {
      // Force the granular consent screen. With include_granted_scopes=true
      // (set on the backend), Google would otherwise silently re-issue a prior
      // partial grant without re-showing the boxes — stranding anyone who left
      // one unchecked. Forcing consent guarantees they can approve the full set.
      await scopeService.requestScopes(clusters, '/onboarding', true)
    } catch (error) {
      console.error('Failed to request scopes:', error)
      setError(
        getApiErrorMessage(error, "We couldn't start Google sign-in. Please try again."),
      )
      setLoading(false)
    }
  }

  const handleToggleMeet = (): void => {
    const newValue = !includeMeet
    setIncludeMeet(newValue)
    trackEvent(OnboardingWizardEvents.PERMISSIONS_MEET_TOGGLED, { enabled: newValue })
  }

  const handleToggleDocs = (): void => {
    const newValue = !includeDocs
    setIncludeDocs(newValue)
    trackEvent(OnboardingWizardEvents.PERMISSIONS_DOCS_TOGGLED, { enabled: newValue })
  }

  if (hasMailCalendar) {
    return (
      <div className="text-center">
        <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <Check className="w-8 h-8 text-green-600" />
        </div>
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Permissions granted</h2>
        <p className="text-gray-600 mb-6">
          Your Google account is connected. Let's set up your assistant.
        </p>
        <button
          onClick={onContinue}
          className="w-full py-3 bg-gradient-to-r from-primary-500 to-primary-600 text-white rounded-xl font-semibold shadow-soft hover:shadow-soft-lg transition-[background-color,box-shadow,transform] duration-200 active:scale-[0.96]"
        >
          Continue
        </button>
      </div>
    )
  }

  const name = isTest ? assistantName || 'Catch' : 'Catch'

  return (
    <div>
      <div className="text-center mb-6">
        {isTest ? (
          <>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">
              Hand {name} your calendar and inbox
            </h2>
            <p className="text-gray-600">
              This is the step that lets {name} take admin off your plate. Without it, {name} can
              chat with you but can&rsquo;t book a meeting or answer an email.
            </p>
          </>
        ) : (
          <>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Connect your Google account</h2>
            <p className="text-gray-600">
              Grant access so Catch can manage your calendar and email.
            </p>
          </>
        )}
      </div>

      {scopeUpgradeFailed && (
        <div className="mb-4">
          <PartialGrantNotice
            incompleteClusters={incompleteClusters}
            footer={(
              <p className="flex items-center gap-1.5 text-xs text-amber-700/90 mt-2">
                <ShieldCheck className="w-4 h-4 flex-shrink-0" />
                <span>
                  Curious what {name} can access? Read our{' '}
                  <a
                    href="/privacy"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="underline font-medium hover:text-amber-900"
                  >
                    Privacy Policy
                  </a>{' '}
                  and{' '}
                  <a
                    href="/terms-new"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="underline font-medium hover:text-amber-900"
                  >
                    Terms
                  </a>
                  .
                </span>
              </p>
            )}
          />
        </div>
      )}

      <div className="space-y-3 mb-6">
        {isTest ? (
          <>
            {/* Mail & Calendar - what access buys them */}
            <div className="p-4 bg-gray-50 rounded-xl border border-gray-200">
              <div className="flex items-center gap-2 mb-3">
                <Calendar className="w-5 h-5 text-primary-500" />
                <Mail className="w-5 h-5 text-primary-500" />
                <span className="font-semibold text-gray-900">With Mail &amp; Calendar, {name}:</span>
              </div>
              <ul className="space-y-2">
                {MAIL_CALENDAR_BENEFITS.map((benefit) => (
                  <li key={benefit} className="flex items-start gap-2 text-sm text-gray-700">
                    <Check className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                    <span>{benefit}</span>
                  </li>
                ))}
              </ul>
            </div>

            <p className="text-xs font-medium text-gray-500 uppercase tracking-wide pt-1">Also connect</p>
          </>
        ) : (
          <div className="flex items-center gap-3 p-4 bg-gray-50 rounded-xl border border-gray-200">
            <div className="flex items-center gap-3 flex-1">
              <div className="flex gap-1.5">
                <Calendar className="w-5 h-5 text-primary-500" />
                <Mail className="w-5 h-5 text-primary-500" />
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <span className="font-semibold text-gray-900">Mail & Calendar</span>
                  <span className="text-xs px-1.5 py-0.5 bg-primary-100 text-primary-700 rounded font-medium">Required</span>
                </div>
                <p className="text-sm text-gray-500 mt-0.5">Manage your schedule and keep your inbox under control</p>
              </div>
            </div>
          </div>
        )}

        {/* Google Meet - Optional */}
        <label className="flex items-center gap-3 p-4 bg-gray-50 rounded-xl border border-gray-200 cursor-pointer hover:bg-gray-100 transition-colors">
          <div className="flex items-center gap-3 flex-1">
            <Video className="w-5 h-5 text-blue-500" />
            <div>
              <span className="font-semibold text-gray-900">Google Meet</span>
              <p className="text-sm text-gray-500 mt-0.5">Track meeting durations and add conference links</p>
            </div>
          </div>
          <input
            type="checkbox"
            checked={includeMeet}
            onChange={handleToggleMeet}
            className="w-5 h-5 rounded border-gray-300 text-primary-500 focus:ring-primary-500 cursor-pointer"
          />
        </label>

        {/* Google Docs - Optional */}
        <label className="flex items-center gap-3 p-4 bg-gray-50 rounded-xl border border-gray-200 cursor-pointer hover:bg-gray-100 transition-colors">
          <div className="flex items-center gap-3 flex-1">
            <FileText className="w-5 h-5 text-yellow-600" />
            <div>
              <span className="font-semibold text-gray-900">Google Docs</span>
              <p className="text-sm text-gray-500 mt-0.5">Access and reference your documents from the assistant</p>
            </div>
          </div>
          <input
            type="checkbox"
            checked={includeDocs}
            onChange={handleToggleDocs}
            className="w-5 h-5 rounded border-gray-300 text-primary-500 focus:ring-primary-500 cursor-pointer"
          />
        </label>
      </div>

      {isTest && (
        <div className="flex items-center gap-3 mb-4 p-3 rounded-xl border border-gray-200">
          <img src="/images/onboarding/soc2.png" alt="SOC 2 Type II" className="h-10 w-10 object-contain flex-shrink-0" />
          <img src="/images/onboarding/casa.png" alt="Google CASA Tier 2 verified" className="h-10 w-10 object-contain flex-shrink-0" />
          <p className="text-xs text-gray-600 leading-relaxed">
            SOC 2 Type II certified and verified by Google (CASA Tier 2). Your data is never used to
            train third-party models, and you can disconnect any time in Settings.
          </p>
        </div>
      )}

      <button
        onClick={() => void handleGrantAccess()}
        disabled={loading}
        className="w-full py-3 bg-gradient-to-r from-primary-500 to-primary-600 text-white rounded-xl font-semibold shadow-soft hover:shadow-soft-lg transition-[background-color,box-shadow,transform] duration-200 active:scale-[0.96] disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
      >
        {loading ? (
          <>
            <Loader2 className="w-5 h-5 animate-spin" />
            Redirecting...
          </>
        ) : (
          isTest ? 'Continue with Google' : 'Grant Access'
        )}
      </button>

      {isTest && (
        <p className="text-center text-xs text-gray-500 mt-3">
          Google will ask you to confirm. Keep every box ticked so {name} can do the job.
        </p>
      )}

      {error && (
        <p className="text-center text-sm text-red-600 mt-3" role="alert">
          {error}
        </p>
      )}

      <PermissionsDeferOption onDefer={onDefer} assistantName={name} collapsed={isTest} />
    </div>
  )
}
